// Library search: a text query plus optional source filters over cards.
//
// Ranking, not plain filtering. Every query word has to match somewhere in
// the card; among the matches, the whole phrase beats separate words, a
// match at the start of a word beats one inside a word, and a match in the
// title weighs more than one in the body. Recency breaks ties.
//
// parseQuery spots source words ("reels", "tweets", "pdfs") and offers them
// as chips. It never filters by itself: the user taps the chip to apply it.
// Pure: safe to import anywhere.

import { byNewest, type LibraryCard } from './libraryCard';
import type { LibrarySource } from './sources';

export interface LibraryFilter {
  query: string;
  /** Empty means every source. */
  sources: LibrarySource[];
}

export const EMPTY_FILTER: LibraryFilter = { query: '', sources: [] };

export function isFilterActive(filter: LibraryFilter): boolean {
  return filter.query.trim().length > 0 || filter.sources.length > 0;
}

function queryWords(query: string): string[] {
  return query.toLowerCase().trim().split(/\s+/).filter(Boolean);
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** 0 = absent, 1 = inside a word, 2 = at a word start, 3 = the whole word. */
function matchStrength(text: string, word: string): number {
  if (!text.includes(word)) return 0;
  const w = escapeRegExp(word);
  if (new RegExp(`(?:^|[^\\p{L}\\p{N}])${w}(?![\\p{L}\\p{N}])`, 'u').test(text)) return 3;
  if (new RegExp(`(?:^|[^\\p{L}\\p{N}])${w}`, 'u').test(text)) return 2;
  return 1;
}

// Title matches count about three times a body match, and a whole phrase
// outranks any number of loose word hits on a short query.
const TITLE_WEIGHT = [0, 3, 7, 9];
const BODY_WEIGHT = [0, 1, 2, 3];
const PHRASE_IN_TITLE = 30;
const PHRASE_IN_BODY = 12;
const TITLE_IS_QUERY = 10;

/**
 * Relevance of a card for the query words, or 0 when some word is missing.
 * Exported for tests.
 */
export function scoreCard(card: LibraryCard, words: string[]): number {
  if (words.length === 0) return 1;
  const title = card.title.toLowerCase();
  const haystack = card.searchText;
  let score = 0;
  for (const word of words) {
    const inTitle = matchStrength(title, word);
    const inBody = inTitle ? 0 : matchStrength(haystack, word);
    if (!inTitle && !inBody) return 0;
    score += TITLE_WEIGHT[inTitle] + BODY_WEIGHT[inBody];
  }
  const phrase = words.join(' ');
  if (words.length > 1) {
    if (title.includes(phrase)) score += PHRASE_IN_TITLE;
    else if (haystack.includes(phrase)) score += PHRASE_IN_BODY;
  }
  if (title.trim() === phrase) score += TITLE_IS_QUERY;
  return score;
}

/**
 * Cards matching the filter, best match first. With no query the order is
 * the input's (newest first); with a query, ties go to the newer card.
 */
export function filterLibraryCards(cards: LibraryCard[], filter: LibraryFilter): LibraryCard[] {
  const sources = new Set(filter.sources);
  const words = queryWords(filter.query);
  const inSource = sources.size > 0 ? cards.filter((c) => sources.has(c.source)) : cards;
  if (words.length === 0) return inSource;
  const scored: { card: LibraryCard; score: number }[] = [];
  for (const card of inSource) {
    const score = scoreCard(card, words);
    if (score > 0) scored.push({ card, score });
  }
  scored.sort((a, b) => b.score - a.score || byNewest(a.card, b.card));
  return scored.map((s) => s.card);
}

// ---- Query parsing ---------------------------------------------------------

const SOURCE_WORDS: Record<string, LibrarySource[]> = {
  reel: ['instagram_reel'],
  reels: ['instagram_reel'],
  instagram: ['instagram_reel', 'instagram_post'],
  insta: ['instagram_reel', 'instagram_post'],
  ig: ['instagram_reel', 'instagram_post'],
  tweet: ['x'],
  tweets: ['x'],
  twitter: ['x'],
  x: ['x'],
  bookmarks: ['x'],
  tiktok: ['tiktok'],
  tiktoks: ['tiktok'],
  youtube: ['youtube'],
  pdf: ['pdf'],
  pdfs: ['pdf'],
  image: ['image'],
  images: ['image'],
  photo: ['image'],
  photos: ['image'],
  picture: ['image'],
  pictures: ['image'],
  video: ['video'],
  videos: ['video'],
  note: ['note'],
  notes: ['note'],
  product: ['product'],
  products: ['product'],
  pin: ['pinterest'],
  pins: ['pinterest'],
  pinterest: ['pinterest'],
};

export interface SourceSuggestion {
  source: LibrarySource;
  /** The word in the query that suggested it, as typed (lowercased). */
  word: string;
}

export interface ParsedQuery {
  words: string[];
  /** Sources the query seems to ask for, in the order their words appear, no duplicates. */
  suggestedSources: SourceSuggestion[];
}

/** Splits the query into words and spots the ones that name a source. */
export function parseQuery(query: string): ParsedQuery {
  const words = queryWords(query);
  const seen = new Set<LibrarySource>();
  const suggestedSources: SourceSuggestion[] = [];
  for (const word of words) {
    // Tolerate a trailing comma or question mark: "reels, cars"
    const bare = word.replace(/[^\p{L}\p{N}]+$/u, '');
    for (const source of SOURCE_WORDS[bare] ?? []) {
      if (seen.has(source)) continue;
      seen.add(source);
      suggestedSources.push({ source, word });
    }
  }
  return { words, suggestedSources };
}

/** The query with the first whole-word occurrence of `word` taken out. */
export function removeQueryWord(query: string, word: string): string {
  const parts = query.trim().split(/\s+/).filter(Boolean);
  const i = parts.findIndex((p) => p.toLowerCase() === word.toLowerCase());
  if (i === -1) return query;
  parts.splice(i, 1);
  return parts.join(' ');
}

/** Applies a tapped suggestion: selects its source and drops its word from the query. */
export function applySuggestion(filter: LibraryFilter, suggestion: SourceSuggestion): LibraryFilter {
  const sources = filter.sources.includes(suggestion.source) ? filter.sources : [...filter.sources, suggestion.source];
  return { query: removeQueryWord(filter.query, suggestion.word), sources };
}

/** Toggles one source in the filter, keeping the others. */
export function toggleSource(filter: LibraryFilter, source: LibrarySource): LibraryFilter {
  const sources = filter.sources.includes(source) ? filter.sources.filter((s) => s !== source) : [...filter.sources, source];
  return { ...filter, sources };
}
